import React, { useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';

// react-pintura
import { PinturaEditor } from '@pqina/react-pintura';

// pintura
import '@pqina/pintura/pintura.css';

import {
  // editor
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,

  // plugins
  setPlugins,
  findNode,
  createMarkupEditorToolbar,
  removeNode,
  createNode,
  getEditorDefaults,
  createMarkupEditorShapeStyleControls,
  createDefaultFontFamilyOptions,
  createDefaultFontSizeOptions,
  createDefaultLineHeightOptions
} from '@pqina/pintura';

import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import { useTranslate } from '@tolgee/react';
import { getLanguage } from '../../../../locales/i18n';
import {
  addCropObject,
  deleteAllOverlays,
  editImageThumbnail,
  replaceMediaAssetFile,
  saveEditorFullImage,
  updateBrandEditorCloseState,
  updateEditorCloseState,
  updateMediaAssetFile,
  updateThumbnailProcessing
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import { Button, ProgressBar } from 'react-bootstrap';
import {
  calculateFontSize,
  checkFontSupportsStyle,
  decimalToRatio,
  getImageDimensions,
  getSmartCrop,
  getValidMimeType,
  hexToNormalizedArray,
  importFont,
  scaleDownImage
} from '../../utils/helpers';
import {
  EditorImageConfig,
  generateFirebaseUploadUrl
} from '../../../../redux/slices/lomavisCreator/utils';
import axios from 'axios';
import axiosClient from '../../../../utils/axios';
import toast from 'react-hot-toast';
import {
  EDITOR_VIEW,
  POST_PUBLISHED_ON_PLATFORM,
  UPLOAD_VIEW
} from '../../config/constants';
import Carousel from './Carousel';
import { FACEBOOK, UPLOAD_TYPE_POST_MEDIA } from '../../../../config/constants';
import {
  CROP_RATIOS,
  CROP_RATIO_CUSTOM,
  POST_TYPE_KEY,
  platformFeatures
} from '../../config/platformFeatures';
import {
  calculateAlignmentX,
  calculateAlignmentY,
  calculateEditorFontSize,
  convertLogo,
  convertOverlayColors,
  loadFont
} from '../../utils/brandOverlay';
import CropSelector from '../MultiImageAutoModifier/cropSelector';
import { DEFAULT_EDITOR_FONT_URL } from '../contants';

setPlugins(EditorImageConfig.plugins);

const getLocale = (lang) => {
  switch (lang) {
    case 'de':
    case 'de-DE':
      return {
        ...EditorImageConfig.DeLocale
      };
    case 'en':
    case 'en-GB':
    default:
      return {
        ...EditorImageConfig.EnLocale
      };
  }
};

const editorDefaults = getEditorDefaults({
  utils: [
    'crop',
    'finetune',
    'filter',
    'annotate',
    'sticker',
    'fill',
    'frame',
    'redact'
  ]
});

const browse = (options) => {
  return new Promise((resolve, reject) => {
    const element = document.createElement('input');
    element.type = 'file';
    element.accept = options.accept;
    element.onchange = () => {
      const [file] = element.files;
      if (!file) return resolve(null);
      resolve(file);
    };
    element.click();
  });
};
const stringifyImageState = (imageState) => {
  return JSON.stringify(imageState, (k, v) => (v === undefined ? null : v));
};

export default function Editor({
  platform,
  startSubmission,
  footerRef,
  rightMargin,
  setStartSubmission,
  mediaFiles,
  handleClose,
  fileInputRef,
  successNotification,
  setSelectedIndex,
  selectedIndex,
  cropMode,
  updateCurrentCrop
}) {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [locale, setLocale] = useState(getLocale(getLanguage()));
  const editorRef = useRef(null);
  const [storedImageState, setStoredImageState] = useState(
    '{"cropLimitToImage": false }'
  );
  const selectedFile = mediaFiles[selectedIndex];
  const preferredMimeType = getValidMimeType(selectedFile.file_type);

  const [processingImage, setProcessingImage] = useState(false);

  const [progress, setProgress] = useState(0);

  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState(EDITOR_VIEW);
  const [currentImage, setCurrentImage] = useState('');
  const [editorUpdateCount, setEditorUpdateCount] = useState(0);
  const [editorChanged, setEditorChanged] = useState(false);

  const location_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.locationUuid
  );

  const { overlayData, brandData, locationUuid } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );

  const cloud_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.cloudUuid
  );
  const postDataCropList = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData?.[platform]?.cropList
  );

  const handleInputChange = (e) => {
    // Exit if no files selected
    const fileList = e.target.files; // This is a FileList
    const fileArray = Array.from(fileList); // Convert the FileList to an array

    fileArray.map(async (file: any) => {
      let preview_image: any = URL.createObjectURL(file);

      let assetBlob: any = URL.createObjectURL(file);

      if (file) {
        try {
          const resizedBlob = await scaleDownImage(file);
          const url = URL.createObjectURL(resizedBlob);
          setUploadedImage(url);
        } catch (error) {
          console.error(error);
          setUploadedImage(preview_image);
        }
      }
    });

    fileInputRef.current.value = '';
  };

  const editor2Ref = useRef(null);
  const handleEditorInit = (editor) => {
    editorRef.current = editor;
  };

  const [addCrop, setAddCrop] = useState(false);
  useEffect(() => {
    setLocale(getLocale(getLanguage()));
  }, [getLanguage()]);

  const editorDefaults = getEditorDefaults({
    ...EditorImageConfig.ImageEditorDefaults,

    locale
  });

  const preview_image = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].uploadedImageThumbnail.url
  );

  const exportImage = async () => {
    setProcessingImage(true);
    if (editorRef.current) {
      const editor = editorRef.current;
      const editorHistory =
        editorRef.current.history.get()[
          editorRef.current.history.get().length - 1
        ];

      // if (
      //   [
      //     'annotation',
      //     'decoration',
      //     'manipulation',
      //     'redaction',
      //     'selection'
      //   ].every((key) => editorHistory?.[key]?.length === 0) &&
      //   !editorHistory?.colorMatrix &&
      //   !editorHistory?.frame
      // ) {
      //   setProcessingImage(false);
      //   setStartSubmission(true);

      //   setCurrentView(UPLOAD_VIEW);
      //   dispatch(
      //     addCropObject({
      //       platform: platform,
      //       cropInfo: {
      //         width: editorHistory?.crop?.width, // width of the selected crop
      //         height: editorHistory?.crop?.height, // height of the selected crop
      //         x: editorHistory?.crop?.x, // start x coordinate of the crop starting
      //         y: editorHistory?.crop?.y, // start y coordinate of the crop starting
      //         uuid: selectedFile?.uuid, // uuid of the image the crop belongs to
      //         aspect_ratio_width: decimalToRatio(editorHistory?.cropAspectRatio)
      //           .width, // select crop aspect ratio width
      //         aspect_ratio_height: decimalToRatio(
      //           editorHistory?.cropAspectRatio
      //         ).height // selected crop aspect ratio height
      //       }
      //     })
      //   );

      //   setStartSubmission(false);
      //   setEditorUpdateCount(0);

      //   dispatch(
      //     updateBrandEditorCloseState({
      //       state: true
      //     })
      //   );
      //   setEditorChanged(false);
      //   successNotification();
      //   setCurrentView(EDITOR_VIEW);
      //   setProgress(0);
      //   return;
      // }

      // Get the edited image

      editor.processImage().then(async (result) => {
        // Handle the resulting image data (base64 or blob)
        const editedImage = result.dest; // This will be a blob

        const url = URL.createObjectURL(editedImage);

        const dimensions = await getImageDimensions(url);
        setCurrentImage(url);
        setProcessingImage(false);
        setStartSubmission(true);
        // Do something with the URL, e.g., display it, download it, etc.

        const uploadPreset = await generateFirebaseUploadUrl(
          {
            content_type: preferredMimeType,
            filesize_in_bytes: editedImage.size,
            filename: selectedFile.name,
            location_uuid: location_uuid ? location_uuid : null,
            cloud_uuid: cloud_uuid ? cloud_uuid : null,
            width: dimensions?.width,
            height: dimensions?.height,
            upload_type: UPLOAD_TYPE_POST_MEDIA
          },
          {},
          () => console.log('error here')
        );

        setCurrentView(UPLOAD_VIEW);

        putWithFile(
          uploadPreset?.url,
          editedImage,
          uploadPreset?.headers,
          preferredMimeType,
          uploadPreset
        )
          .then((responseData) => {
            setStartSubmission(false);
            dispatch(
              replaceMediaAssetFile({
                original_uuid: selectedFile?.uuid,
                uuid: uploadPreset?.uuid,
                preview_image: url,
                file: editedImage,
                assetBlob: url,
                platform: platform,
                width: dimensions?.width,
                height: dimensions?.height,
                uploadPresets: uploadPreset,
                source_media_uuid: selectedFile.source_media_uuid
                  ? selectedFile.source_media_uuid
                  : selectedFile?.uuid
              })
            );
            dispatch(deleteAllOverlays());
            setEditorUpdateCount(0);
            dispatch(
              updateBrandEditorCloseState({
                state: false
              })
            );
            setEditorChanged(false);
            successNotification();
            setCurrentView(EDITOR_VIEW);
            cropMode && handleClose();

            setProgress(0);
          })
          .catch((error) => {
            setStartSubmission(false);
            console.error('Upload failed:', error);
            setProgress(0);
            setCurrentView(EDITOR_VIEW);
          });
      });
    }
  };

  const putWithFile = async (url, file, headers, file_type, uploadPreset) => {
    console.warn('new upload', uploadPreset);
    try {
      const config = {
        headers: headers ? { ...headers, 'Content-Type': file_type } : {} // Set headers property with provided headers
      };
      const response = await axios.put(url, file, {
        ...config,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        }
      });

      try {
        const res = await axiosClient.put(uploadPreset?.confirm_url, {
          success: true
        });

        return res.data;
      } catch (err: any) {
        console.log(err);
      }
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
      } else {
        const res = error.response;

        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Looks like there was a problem. Status Code: ${error}`);

        console.error('Error:', error);
        throw error;
      }
      toast.error(` ${t('errors.request_couldnt_be_completed')}`, {
        duration: 4000,
        position: 'top-right'
      });

      // Dispatch deleteAfile action inside the catch block
    }
  };

  const willRenderToolbar = (toolbar) => {
    // get the export button
    const exportButton = findNode('export', toolbar);

    removeNode('export', toolbar);

    return toolbar;
  };
  ///////////////////////////////

  const handleEditorUpdate = async (imageState) => {
    console.log('updates', imageState);
    setEditorUpdateCount((prevState) => prevState + 1);

    const imageStateStr = stringifyImageState(imageState);

    setStoredImageState(imageStateStr);

    if (editorRef.current?.history) {
      const prevEditorHistory =
        editorRef.current?.history?.get()[
          editorRef.current?.history?.get().length - 1
        ];

      if (imageState?.cropAspectRatio !== prevEditorHistory?.cropAspectRatio) {
        if (imageState?.cropAspectRatio === undefined) {
          handleCropChange(imageState);
        }
      }
    }
  };

  const selectAnImage = async () => {
    const annotations = editorRef.current.editor.imageAnnotation;
    const shape = annotations.find((shape) => shape.id === '');

    // browse for an image
    const file: any = await browse({
      accept: 'image/*'
    });

    // no file selected
    if (!file) return;

    // update background image
    shape.backgroundImage = URL.createObjectURL(file);

    // redraw annotations
    editorRef.current.editor.imageAnnotation = annotations;
  };
  const handleEditorProcess = (res) => {
    // 1. Turn imageState into string, replaces undefined values with null
    const imageStateStr = stringifyImageState(res.imageState);
    setStoredImageState(imageStateStr);
  };
  function removeObjectsByKeys(object, keysToRemove) {
    return Object.keys(object).reduce((result, key) => {
      if (!keysToRemove.includes(key)) {
        result[key] = object[key];
      }
      return result;
    }, {});
  }
  const handleImageUpdate = async ({
    uploadedImage,
    storedImageState,
    coordinate = null,
    availableDimension = null,
    additionalObj = {},
    aspectRatio = undefined
  }) => {
    if (!uploadedImage || !storedImageState) {
      return;
    }
    console.log('current version', JSON.parse(storedImageState));

    const dimensions = availableDimension
      ? availableDimension
      : await getImageDimensions(uploadedImage);

    const currentState = JSON.parse(storedImageState);
    const justAnnotations = [...currentState?.annotation];

    const filteredState = removeObjectsByKeys(currentState, ['annotation']);
    const newAnnote = {
      aspectRatio: aspectRatio,
      backgroundColor: [0, 0, 0, 0],
      backgroundImage: uploadedImage,
      backgroundSize: 'contain',
      cornerRadius: 0,
      disableErase: true,
      disableStyle: [
        'cornerRadius',
        'backgroundColor',
        'strokeColor',
        'strokeWidth'
      ],
      height: dimensions?.height,
      id: uploadedImage,
      isEditing: false,
      isSelected: true,
      opacity: 1,
      rotation: 0,
      strokeColor: [0, 0, 0],
      strokeWidth: 0,
      width: dimensions?.width,
      x: coordinate ? coordinate.x : 0,
      y: coordinate ? coordinate.y : 0,
      ...additionalObj
    };
    const combined = {
      ...filteredState,
      annotation: [...justAnnotations, newAnnote]
    };

    const imageStateStr = stringifyImageState(combined);
    editorRef.current.history.write(JSON.parse(imageStateStr));
    setStoredImageState(imageStateStr);
    editorRef.current.util = undefined;
    editorRef.current.util = 'sticker';
  };

  const handleCropChange = async (imageState) => {
    if (!editorRef.current) return;

    const src = mediaFiles[selectedIndex].assetBlob
      ? mediaFiles[selectedIndex].assetBlob
      : mediaFiles[selectedIndex].original_asset;
    const ImageDimensions = await getImageDimensions(src);

    // If "Custom" is selected, set the crop to the maximum available crop
    if (imageState?.crop) {
      editorRef.current?.history?.write({
        crop: {
          x: 0,
          y: 0,
          width: ImageDimensions.width,
          height: ImageDimensions.height
        }
      });
    }
  };
  const getOverlayDataByPosition = (positionToFind) => {
    return overlayData.find((item) => item.position === positionToFind);
  };
  const getOverlayDataByUuid = (uuid) => {
    return overlayData.find((item) => item?.image === uuid);
  };

  const handleCropUpdate = async () => {
    if (!storedImageState || !editorRef?.current?.history) {
      return;
    }

    const fileCropObj = postDataCropList.find(
      (obj) => obj?.uuid === selectedFile?.uuid
    );
    if (fileCropObj) {
      function removeObjectsByKeys(object, keysToRemove) {
        return Object.keys(object).reduce((result, key) => {
          if (!keysToRemove.includes(key)) {
            result[key] = object[key];
          }
          return result;
        }, {});
      }

      const currentState = JSON.parse(storedImageState);

      const filteredState = removeObjectsByKeys(currentState, [
        'crop',
        'cropAspectRatio'
      ]);

      const combined = {
        ...filteredState,
        crop: {
          height: fileCropObj?.height,
          width: fileCropObj?.width,
          x: fileCropObj?.x,
          y: fileCropObj?.y
        },
        cropAspectRatio:
          fileCropObj?.aspect_ratio_width / fileCropObj?.aspect_ratio_height
      };

      const imageStateStr = stringifyImageState(combined);
      await editorRef.current.history.write(combined);
    }

    //'make overlay update here'

    const checkIfImageIsNotAnEdit = () => {
      const overlayObj = getOverlayDataByUuid(selectedFile.uuid);

      if (overlayObj?.image === selectedFile.uuid) {
        return true;
      } else {
        return false;
      }
    };

    // const checkIfImageIsAnEdit = () => {
    //   const overlayObj = getOverlayDataByPosition(selectedIndex);

    //   if (overlayObj.image === selectedFile.uuid) {
    //     return true;
    //   }

    //   return !selectedFile.source_media_uuid;
    // };

    if (overlayData.length > 0 && checkIfImageIsNotAnEdit()) {
      await uploadOverlay();
      await processLogos();
      updateText();
    }
  };

  const overlayDataObj = getOverlayDataByPosition(selectedIndex);

  const updateText = async () => {
    importFont(DEFAULT_EDITOR_FONT_URL);
    const editorHistory =
      editorRef.current.history.get()[
        editorRef.current.history.get().length - 1
      ];

    importFont(brandData.fonts.fontFaceUrl);
    const src = mediaFiles[selectedIndex].assetBlob
      ? mediaFiles[selectedIndex].assetBlob
      : mediaFiles[selectedIndex].original_asset;
    const ImageDimensions = await getImageDimensions(src);
    const overlayAspectRatio =
      overlayDataObj?.overlay.originalWidth /
      overlayDataObj?.overlay.originalHeight;
    const baseAspectRatio = ImageDimensions.width / ImageDimensions.height;

    let overlayWidth: number, overlayHeight: number;
    if (overlayAspectRatio > baseAspectRatio) {
      overlayWidth = ImageDimensions.width;
      overlayHeight = overlayWidth / overlayAspectRatio;
    } else {
      overlayHeight = ImageDimensions?.height;
      overlayWidth = overlayHeight * overlayAspectRatio;
    }

    const overlayX = calculateAlignmentX(
      overlayDataObj?.overlay.align,
      ImageDimensions.width,
      overlayWidth
    );
    const overlayY = calculateAlignmentY(
      overlayDataObj?.overlay.align,
      ImageDimensions.height,
      overlayHeight
    );

    const justAnnotations = [...editorHistory?.annotation];

    const filteredState = removeObjectsByKeys(editorHistory, ['annotation']);

    const textInfoArray = overlayDataObj?.text;
    const newTextAnnotation = [];
    for (const textItem of textInfoArray) {
      let checkedFontWeight = textItem.fontWeight;
      let checkedFontStyle = textItem.fontStyle;
      await checkFontSupportsStyle(brandData.fonts.fontFaceUrl, {
        weight: textItem.fontWeight,
        style: textItem.fontStyle
      }).then((result) => {
        console.log('font style supports', result); // { weight: false, style: false }
        checkedFontWeight = result.weight ? textItem.fontWeight : 'normal';
        checkedFontStyle = result.style ? textItem.fontStyle : 'normal';
      });

      const { resultFontSize, deviation } = await calculateEditorFontSize(
        mediaFiles[selectedIndex].assetBlob
          ? mediaFiles[selectedIndex].assetBlob
          : mediaFiles[selectedIndex].original_asset,

        textItem,
        brandData
      );
      console.warn(
        'calculated font size',
        resultFontSize,
        deviation,
        checkedFontWeight,
        checkedFontStyle
      );
      const newAnnote = {
        x: `${
          ((overlayX +
            (textItem.x / overlayDataObj?.overlay.originalWidth) *
              overlayWidth) /
            ImageDimensions.width) *
          100
        }%`,
        y: `${(deviation / ImageDimensions.height) * 100}%`,
        text: textItem.text,
        color: hexToNormalizedArray(textItem.color),
        fontSize: resultFontSize,
        fontFamily: brandData.fonts.fontFamily,
        fontWeight: checkedFontWeight,
        fontStyle: checkedFontStyle,
        lineHeight: `${textItem.lineHeight * 100}%`,
        rotation: 0,
        flipX: textItem.flipX ? textItem.flipX : false,
        flipY: textItem.flipY ? textItem.flipY : false,
        opacity: 1,
        id: new Date(),
        // width: `${(textItem.width / ImageDimensions.width) * 100}%`,
        // height: `${(textItem.height / ImageDimensions.height) * 100}%`,
        disableErase: true,
        format: 'text',
        textAlign: textItem.textAlign,
        isSelected: false,
        isEditing: false,
        disableTextScale: true,
        width: textItem?.boxWidth,
        disableNewline: false
      };

      newTextAnnotation.push(newAnnote);
    }

    const combined = {
      ...filteredState,
      annotation: [...justAnnotations, ...newTextAnnotation]
    };

    const imageStateStr = stringifyImageState(combined);
    editorRef.current.history.write(JSON.parse(imageStateStr));
    setStoredImageState(imageStateStr);
    editorRef.current.util = undefined;
    editorRef.current.util = 'sticker';
  };
  const replaceColors = () => {
    if (
      locationUuid &&
      !(selectedFile?.upload_to_platform_status >= POST_PUBLISHED_ON_PLATFORM)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const uploadOverlay = async () => {
    const baseImg = mediaFiles[selectedIndex].assetBlob
      ? mediaFiles[selectedIndex].assetBlob
      : mediaFiles[selectedIndex].original_asset;
    const baseDimension = await getImageDimensions(baseImg);

    const overlayBlob = await convertOverlayColors(
      overlayDataObj,
      brandData,
      baseDimension.width,
      replaceColors()
    );
    const editorHistory =
      editorRef.current.history.get()[
        editorRef.current.history.get().length - 1
      ];
    const src = mediaFiles[selectedIndex].assetBlob
      ? mediaFiles[selectedIndex].assetBlob
      : mediaFiles[selectedIndex].original_asset;

    const editorDimension = await getImageDimensions(src); // { width, height }
    const overlayDimension = {
      width: overlayDataObj?.overlay.originalWidth,
      height: overlayDataObj?.overlay.originalHeight
    };

    // const overlayDimension = await getImageDimensions(
    //   overlayDataObj?.overlay.url
    // );

    const editorAspectRatio = editorDimension.width / editorDimension.height;
    const overlayAspectRatio = overlayDimension.width / overlayDimension.height;

    let scaledWidth, scaledHeight;

    // Scale overlay to fit within editor dimensions while maintaining aspect ratio
    if (overlayAspectRatio > editorAspectRatio) {
      // Overlay is wider than editor; fit based on width
      scaledWidth = editorDimension.width;
      scaledHeight = scaledWidth / overlayAspectRatio;
    } else {
      // Overlay is taller than editor; fit based on height
      scaledHeight = editorDimension.height;
      scaledWidth = scaledHeight * overlayAspectRatio;
    }

    // Center the overlay within the editor
    const centeredX = (editorDimension.width - scaledWidth) / 2;
    const centeredY = (editorDimension.height - scaledHeight) / 2;
    handleImageUpdate({
      uploadedImage: URL.createObjectURL(overlayBlob),
      storedImageState: stringifyImageState(editorHistory),
      availableDimension: {
        width: scaledWidth,
        height: scaledHeight
      },
      coordinate: {
        x: centeredX,
        y: centeredY
      },

      additionalObj: {
        disableSelect: true,
        disableManipulate: true,
        disableMove: true,
        disableResize: true,
        disableRotate: true
      }
    });
  };
  async function processLogos() {
    const src = mediaFiles[selectedIndex].assetBlob
      ? mediaFiles[selectedIndex].assetBlob
      : mediaFiles[selectedIndex].original_asset;

    const ImageDimensions = await getImageDimensions(src);

    const overlayAspectRatio =
      overlayDataObj?.overlay.originalWidth /
      overlayDataObj?.overlay.originalHeight;
    const baseAspectRatio = ImageDimensions.width / ImageDimensions.height;

    let overlayWidth: number, overlayHeight: number;
    if (overlayAspectRatio > baseAspectRatio) {
      overlayWidth = ImageDimensions.width;
      overlayHeight = overlayWidth / overlayAspectRatio;
    } else {
      overlayHeight = ImageDimensions?.height;
      overlayWidth = overlayHeight * overlayAspectRatio;
    }

    const overlayX = calculateAlignmentX(
      overlayDataObj?.overlay.align,
      ImageDimensions.width,
      overlayWidth
    );
    const overlayY = calculateAlignmentY(
      overlayDataObj?.overlay.align,
      ImageDimensions.height,
      overlayHeight
    );
    try {
      const logoBlobs = await convertLogo(src, overlayDataObj, brandData);
      console.log('Converted logo blobs:', logoBlobs);

      // You can now use the blobs, for example, create Object URLs for preview
      logoBlobs.forEach(({ blob, index }) => {
        const url = URL.createObjectURL(blob);
        console.log(`Blob URL ${index + 1}:`, url);
        const editorHistory =
          editorRef.current.history.get()[
            editorRef.current.history.get().length - 1
          ];

        const calcWidth =
          (overlayDataObj?.logo[index].width /
            overlayDataObj?.overlay.originalWidth) *
          ImageDimensions.width;
        const calcHeight =
          (overlayDataObj?.logo[index].height /
            overlayDataObj?.overlay.originalHeight) *
          ImageDimensions.height;

        const calcX =
          overlayX +
          (overlayDataObj?.logo[index].x /
            overlayDataObj?.overlay.originalWidth) *
            overlayWidth;
        const calcY =
          overlayY +
          (overlayDataObj?.logo[index].y /
            overlayDataObj?.overlay.originalHeight) *
            overlayHeight;

        handleImageUpdate({
          uploadedImage: url,
          storedImageState: stringifyImageState(editorHistory),

          coordinate: {
            x: calcX,
            y: calcY
          },
          availableDimension: {
            width: calcWidth,
            height: calcHeight
          },
          aspectRatio: calcWidth / calcHeight
        });
      });
    } catch (error) {
      console.error('Error converting logos:', error);
    }
  }

  const fetchCrops = async (item) => {
    let aspectRatio = CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height;

    let crop = async () => {
      if (!aspectRatio) {
        return undefined;
      } else {
        return await getSmartCrop(
          mediaFiles[selectedIndex].assetBlob
            ? mediaFiles[selectedIndex].assetBlob
            : mediaFiles[selectedIndex].original_asset,
          CROP_RATIOS[item]?.width,
          CROP_RATIOS[item]?.height
        );
      }
    };
    const newCrop = await crop();
    // Update only the editor that corresponds to the given `Editor`
    editorRef.current?.history?.write;
    if (editorRef?.current) {
      editorRef.current.history.write({
        crop: newCrop,
        cropAspectRatio: CROP_RATIOS[item]?.width / CROP_RATIOS[item]?.height
      });
    }
  };

  useEffect(() => {
    if (!cropMode) {
      return;
    }

    fetchCrops(mediaFiles[selectedIndex].defaultCropRatio);
  }, [
    mediaFiles[selectedIndex].cropSuggestions,
    mediaFiles[selectedIndex].defaultCropRatio
  ]);

  useEffect(() => {
    handleImageUpdate({
      uploadedImage: uploadedImage,
      storedImageState: storedImageState
    });
  }, [uploadedImage]);
  useEffect(() => {
    setEditorUpdateCount(0);
  }, [selectedFile]);

  const typeKey = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postTypeSelected
  );

  const postTypeRule = (platform) => {
    return platformFeatures[platform][POST_TYPE_KEY]?.find(
      (pt) => pt.typeKey === typeKey[platform]
    );
  };

  useEffect(() => {
    if (editorRef !== null) {
      handleCropUpdate();
    }
  }, [platform]);

  return (
    <div className="d-flex w-100 h-100 flex-column gap-4">
      <div className="d-flex w-100 h-100 gap-4">
        {' '}
        {cropMode ? (
          <div
            style={{ overflowY: 'auto', width: '100px' }}
            className="bg-white p-2 h-100 rounded-3 mx-auto"
          >
            <CropSelector
              items={mediaFiles[selectedIndex].cropSuggestions}
              updateCurrentCrop={updateCurrentCrop}
              selectedCrop={mediaFiles[selectedIndex].defaultCropRatio}
              platform={mediaFiles[selectedIndex].platform}
            />
          </div>
        ) : (
          <div
            style={{ overflowY: 'auto', width: '140px' }}
            className="bg-white p-2 h-100 rounded-3 mx-auto"
          >
            <Carousel
              items={mediaFiles}
              setSelectedIndex={setSelectedIndex}
              selectedFile={selectedFile}
              setEditorChanged={setEditorChanged}
              editorChanged={editorChanged}
            />
          </div>
        )}
        {selectedFile && (
          <div
            // style={{ minHeight: "500px" }}
            className="w-100 h-100 flex-fill"
          >
            <div className=" w-100 h-100 position-relative d-flex flex-column gap-4 ">
              {processingImage && (
                <div
                  style={{ zIndex: 10, top: '0px', left: 'Opx' }}
                  className="position-absolute w-100 h-100 bg-white bg-opacity-10% rounded-3"
                ></div>
              )}
              {!editorChanged && (
                <div
                  onClick={() => {
                    dispatch(
                      updateBrandEditorCloseState({
                        state: true
                      })
                    );
                    setEditorChanged(true);
                  }}
                  style={{ zIndex: 11, top: '0px', left: 'Opx' }}
                  className="position-absolute w-100 h-100 bg-opacity-0% rounded-3"
                ></div>
              )}
              {currentView === EDITOR_VIEW && (
                <div className="w-100 bg-white p-3 h-100 rounded-3">
                  <PinturaEditor
                    {...editorDefaults}
                    ref={editorRef}
                    stickers={[
                      ['Emoji', [...EditorImageConfig?.Stickers]],
                      ['Markers', []]
                    ]}
                    cropSelectPresetOptions={[
                      [
                        'Crop',

                        postTypeRule(
                          FACEBOOK
                        ).mediaTypes?.image?.cropSuggestions?.map((item) => {
                          if (item === '0:0' || !item) {
                            return [
                              undefined,
                              t('lomavisCreator.editorCropRatio.Custom')
                            ];
                          } else {
                            return [
                              CROP_RATIOS[item]?.width /
                                CROP_RATIOS[item]?.height,
                              `${CROP_RATIOS[item]?.width}:${CROP_RATIOS[item]?.height}`
                            ];
                          }
                        })
                      ]
                    ]}
                    src={
                      mediaFiles[selectedIndex].assetBlob
                        ? mediaFiles[selectedIndex].assetBlob
                        : mediaFiles[selectedIndex].original_asset
                    }
                    onInit={handleEditorInit}
                    enableZoom={false}
                    enableZoomControls={false}
                    onReady={handleCropUpdate}
                    cropInteractionFocus={'selection'}
                    willRenderToolbar={willRenderToolbar}
                    stickerStickToImage={true}
                    onUpdate={handleEditorUpdate}
                    onProcess={handleEditorProcess}
                    markupEditorShapeStyleControls={createMarkupEditorShapeStyleControls(
                      {
                        fontFamilyOptions: [
                          // Add our custom option
                          [
                            brandData.fonts.fontFamily,
                            brandData.fonts.fontFamily
                          ],

                          // Add the default options
                          ...createDefaultFontFamilyOptions()
                        ],
                        // Set absolute font size values
                        fontSizeOptions: createDefaultFontSizeOptions(),

                        // Set absolute line height values
                        lineHeightOptions: createDefaultLineHeightOptions()
                      }
                    )}
                  ></PinturaEditor>
                </div>
              )}

              {currentView === UPLOAD_VIEW && (
                <div className="w-100 bg-white p-3 h-100 rounded-3">
                  <div
                    style={{
                      width: '80%',
                      height: '90%'
                    }}
                    className="mx-auto align-items-center justify-content-center d-flex flex-column gap-4"
                  >
                    <div className="w-100 h-100 position-relative">
                      <img
                        style={{
                          maxHeight: '100%',
                          maxWidth: '100%'
                        }}
                        className="position-absolute top-50 start-50 translate-middle object-fit-contain"
                        src={currentImage}
                        alt="Image preview"
                      />
                    </div>
                    <div style={{ height: '14px' }} className="w-50">
                      {progress > 0 && (
                        <ProgressBar
                          className="h-100"
                          variant="primary"
                          animated
                          now={progress}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        ref={footerRef}
        style={{ bottom: '0', zIndex: 100 }}
        className="w-100 d-flex justify-content-between gap-3 rounded-3 align-items-center bg-white py-2 px-3 border-top h-60px bg-white"
      >
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            max={1}
            style={{ display: 'none' }}
          />
        </div>
        <div
          style={{ marginRight: rightMargin + 'px' }}
          className="d-flex justify-content-end gap-3 align-items-center py-2 flax-fill"
        >
          {!startSubmission && !processingImage && (
            <Button
              onClick={() => {
                currentView === UPLOAD_VIEW
                  ? setCurrentView(EDITOR_VIEW)
                  : handleClose();
              }}
              variant="secondary"
              size="sm"
              disabled={startSubmission || processingImage}
            >
              {t('lomavisCreator.back')}
            </Button>
          )}

          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              exportImage();
            }}
            disabled={startSubmission || processingImage}
          >
            {(startSubmission || processingImage) && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
            )}
            {startSubmission || processingImage
              ? t('lomavisCreator.buttons.saving')
              : t('lomavisCreator.buttons.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
